import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createLabourItemGroup,
  updateLabourItemGroup,
  CreateLabourItemGroupRequest,
  UpdateLabourItemGroupRequest,
} from '../api/labourItemGroupsApi';
import { onboardingKeys } from '../../onboarding/hooks/onboardingKeys';
import { labourGroupKeys } from './labourGroupKeys';

interface UnifiedGroupMutationRequest {
  groupId?: string; // If provided, update; if not, create
  name: string;
  labourItemDefinitionIds?: string[];
}

/**
 * Unified mutation hook for creating and updating labour item groups.
 *
 * Errors are handled by the global QueryClient onError (shows snackbar).
 * Callers can override with mutate(data, { onSuccess, onError }).
 */
export const useUnifiedGroupMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UnifiedGroupMutationRequest) => {
      if (request.groupId) {
        // Update existing group
        const updateRequest: UpdateLabourItemGroupRequest = {
          name: request.name,
          labourItemDefinitionIds: request.labourItemDefinitionIds,
        };
        await updateLabourItemGroup(request.groupId, updateRequest);
        return undefined;
      } else {
        // Create new group
        const createRequest: CreateLabourItemGroupRequest = {
          name: request.name,
          labourItemDefinitionIds: request.labourItemDefinitionIds,
        };
        return await createLabourItemGroup(createRequest);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: labourGroupKeys.all });
      queryClient.invalidateQueries({ queryKey: labourGroupKeys.details() });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.status() });
    },
  });
};
